import { Box, Tab, Tabs } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { routes } from 'utils/constants/routes';
import { languages } from 'utils/redux/reducer/authentication-slice';
import { TabPanel } from 'utils/validators/HelperComponents';
import { ScrollTop } from 'utils/validators/HelperFunctions';
import strings from '../../../../../../common/Translation/Translate';
import Config from '../../../../../../utils/Config';
import axiosInstance from '../../../../../../utils/axios';
import { URLS } from '../../../../../../utils/constants/urls';
import AddEdit from '../../../../CommonPage/AddEdit';
import { LocalizedAttributeForm } from './LocalizedAttributeForm';

export const LocalizedAttribute = () => {
  const { id } = useParams() as any;
  const navigate = useNavigate();
  const languageList = useSelector(languages) as any;
  const [innerTabValue, setInnerTabValue] = useState(0);
  const [pageResponse, setPageResponse] = useState<any>({});

  const [menuPlanDeleteFile, setMenuPlanDeleteFile] = useState<any>([]);
  const [contractTemplateDeleteFile, setContractTemplateDeleteFile] = useState<any>([]);
  const [beoTemplateDeleteFile, setBeoTemplateDeleteFile] = useState<any>([]);
  const [invoiceTemplateDeleteFile, setInvoiceTemplateDeleteFile] = useState<any>([]);
  const [proposalTemplateDeleteFile, setProposalTemplateDeleteFile] = useState<any>([]);
  const [enquiryTemplateDeleteFile, setEnquiryTemplateDeleteFile] = useState<any>([]);
  const [piTemplateDeleteFile, setPiTemplateDeleteFile] = useState<any>([]);
  const [corporateRateTemplateDeleteFile, setCorporateRateTemplateDeleteFile] = useState<any>([]);

  const handleChange = (event: React.SyntheticEvent, newValue: any) => {
    setInnerTabValue(newValue);
  };

  const getPageData = useCallback(async () => {
    try {
      const pageId = id;
      const { data } = await axiosInstance.get(URLS.getOrgLocalizedInfoById(pageId));
      const langSpecificData = data?.localizedAttributes[languageList?.[innerTabValue]?.value];
      const contractTemplates = [];
      if (langSpecificData?.contractTemplate) {
        contractTemplates.push({
          id: pageId,
          name: langSpecificData?.contractTemplateFileName ?? 'Contract',
          type: langSpecificData?.contractTemplateFileType ?? 'text/html',
          preview: langSpecificData?.contractTemplate ? Config.BaseURL + langSpecificData?.contractTemplate : null,
        });
      }

      const beoTemplates = [];
      if (langSpecificData?.beoTemplate) {
        beoTemplates.push({
          id: pageId,
          name: langSpecificData?.beoTemplateFileName ?? 'BEO',
          type: langSpecificData?.beoTemplateFileType ?? 'text/html',
          preview: langSpecificData?.beoTemplate ? Config.BaseURL + langSpecificData?.beoTemplate : null,
        });
      }

      const invoiceTemplates = [];
      if (langSpecificData?.invoiceTemplate) {
        invoiceTemplates.push({
          id: pageId,
          name: langSpecificData?.invoiceTemplateFileName ?? 'Invoice',
          type: langSpecificData?.invoiceTemplateFileType ?? 'text/html',
          preview: langSpecificData?.invoiceTemplate ? Config.BaseURL + langSpecificData?.invoiceTemplate : null,
        });
      }

      const menuPlans = [];
      if (langSpecificData?.menuPlan) {
        menuPlans.push({
          id: pageId,
          name: langSpecificData?.menuPlanFileName ?? 'MenuPlan',
          type: langSpecificData?.menuPlanFileType ?? 'text/pdf',
          preview: langSpecificData?.menuPlan ? Config.BaseURL + langSpecificData?.menuPlan : null,
        });
      }

      const proposalTemplates = [];
      if (langSpecificData?.proposalTemplate) {
        proposalTemplates.push({
          id: pageId,
          name: langSpecificData?.proposalTemplateFileName ?? 'Invoice',
          type: langSpecificData?.proposalTemplateFileType ?? 'text/html',
          preview: langSpecificData?.proposalTemplate ? Config.BaseURL + langSpecificData?.proposalTemplate : null,
        });
      }

      const piTemplates = [];
      if (langSpecificData?.piTemplate) {
        piTemplates.push({
          id: pageId,
          name: langSpecificData?.piTemplateFileName ?? 'Proforma Invoice',
          type: langSpecificData?.piTemplateFileType ?? 'text/html',
          preview: langSpecificData?.piTemplate ? Config.BaseURL + langSpecificData?.piTemplate : null,
        });
      }

      const corporateRateTemplates = [];
      if (langSpecificData?.corporateRateTemplate) {
        corporateRateTemplates.push({
          id: pageId,
          name: langSpecificData?.corporateRateTemplateFileName ?? 'Corporate Rates',
          type: langSpecificData?.corporateRateTemplateFileType ?? 'text/html',
          preview: langSpecificData?.corporateRateTemplate
            ? Config.BaseURL + langSpecificData?.corporateRateTemplate
            : null,
        });
      }

      const enquiryTemplates = [];
      if (langSpecificData?.enquiryTemplate) {
        enquiryTemplates.push({
          id: pageId,
          name: langSpecificData?.enquiryTemplateFileName ?? 'Enquiry',
          type: langSpecificData?.enquiryTemplateFileType ?? 'text/html',
          preview: langSpecificData?.enquiryTemplate ? Config.BaseURL + langSpecificData?.enquiryTemplate : null,
        });
      }

      setPageResponse({
        ...langSpecificData,
        contractTemplate: contractTemplates,
        beoTemplate: beoTemplates,
        menuPlan: menuPlans,
        invoiceTemplate: invoiceTemplates,
        proposalTemplate: proposalTemplates,
        enquiryTemplate: enquiryTemplates,
        piTemplate: piTemplates,
        corporateRateTemplate: corporateRateTemplates,
      });
    } catch (error: any) {
      console.log(error);
    }
  }, [id, innerTabValue, languageList]);

  useEffect(() => {
    (async () => {
      if (id) {
        await getPageData();
      }
    })();
  }, [getPageData, id]);

  const pageConfig = {
    title: id ? strings.organization_text_edit_org : strings.organization_text_add_org,
    successMessage: id ? strings.common_update_message : strings.common_save_message,
    addEditApiUrl: `${URLS.getOrgLocalizedInfoById(id)}/${languageList?.[innerTabValue]?.value}`,
    formModel: LocalizedAttributeForm(),
    navigateToPage: '',
    isCustomResponse: true,
    isFormData: false,
    hideClose: true,
  };

  const deleteImages = (key: string, value: string, formData: any, isDeleted: any) => {
    if (Array.isArray(isDeleted) && isDeleted[0].id) {
      const name = isDeleted[0].name;
      switch (key) {
        case 'contractTemplate':
          setContractTemplateDeleteFile(name);
          setPageResponse({ ...pageResponse, contractTemplate: '' });
          break;
        case 'beoTemplate':
          setBeoTemplateDeleteFile(name);
          setPageResponse({ ...pageResponse, beoTemplate: '' });
          break;
        case 'invoiceTemplate':
          setInvoiceTemplateDeleteFile(name);
          setPageResponse({ ...pageResponse, invoiceTemplate: '' });
          break;
        case 'menuPlan':
          setMenuPlanDeleteFile(name);
          setPageResponse({ ...pageResponse, menuPlan: '' });
          break;
        case 'proposalTemplate':
          setProposalTemplateDeleteFile(name);
          setPageResponse({ ...pageResponse, proposalTemplate: '' });
          break;
        case 'piTemplate':
          setPiTemplateDeleteFile(name);
          setPageResponse({ ...pageResponse, piTemplate: '' });
          break;
        case 'corporateRateTemplate':
          setCorporateRateTemplateDeleteFile(name);
          setPageResponse({ ...pageResponse, corporateRateTemplate: '' });
          break;
        case 'enquiryTemplate':
          setEnquiryTemplateDeleteFile(name);
          setPageResponse({ ...pageResponse, enquiryTemplate: '' });
          break;
        default:
          break;
      }
    }
  };

  const getCustomValueOne = (value: any) => value ?? '';

  function handleCustomResponseHelper(custom: any, body: any) {
    const getCustomValueTwo = (key: any) => custom[key] ?? pageResponse[key];

    if (getCustomValueTwo('contractTemplate')) {
      getCustomValueTwo('contractTemplate').forEach((file: any) => {
        body.append('contractTemplate', file);
      });
    } else {
      body.append('contractTemplate', null as any);
    }

    if (getCustomValueTwo('beoTemplate')) {
      getCustomValueTwo('beoTemplate').forEach((file: any) => {
        body.append('beoTemplate', file);
      });
    } else {
      body.append('beoTemplate', null as any);
    }

    if (getCustomValueTwo('invoiceTemplate')) {
      getCustomValueTwo('invoiceTemplate').forEach((file: any) => {
        body.append('invoiceTemplate', file);
      });
    } else {
      body.append('invoiceTemplate', null as any);
    }

    if (getCustomValueTwo('proposalTemplate')) {
      getCustomValueTwo('proposalTemplate').forEach((file: any) => {
        body.append('proposalTemplate', file);
      });
    } else {
      body.append('proposalTemplate', null as any);
    }

    if (getCustomValueTwo('enquiryTemplate')) {
      getCustomValueTwo('enquiryTemplate').forEach((file: any) => {
        body.append('enquiryTemplate', file);
      });
    } else {
      body.append('enquiryTemplate', null as any);
    }

    if (getCustomValueTwo('piTemplate')) {
      getCustomValueTwo('piTemplate').forEach((file: any) => {
        body.append('piTemplate', file);
      });
    } else {
      body.append('piTemplate', null as any);
    }

    if (getCustomValueTwo('corporateRateTemplate')) {
      getCustomValueTwo('corporateRateTemplate').forEach((file: any) => {
        body.append('corporateRateTemplate', file);
      });
    } else {
      body.append('corporateRateTemplate', null as any);
    }

    if (getCustomValueTwo('menuPlan')) {
      getCustomValueTwo('menuPlan').forEach((file: any) => {
        body.append('menuPlan', file);
      });
    } else {
      body.append('menuPlan', null as any);
    }
  }

  const handleCustomResponse = (custom: any) => {
    const body = new FormData();
    body.append('description', getCustomValueOne(custom.description));
    body.append('roomReservationText', getCustomValueOne(custom.roomReservationText));
    body.append('rfqTerms', getCustomValueOne(custom.rfqTerms));
    handleCustomResponseHelper(custom, body);
    body.append('deletedMenuPlan', menuPlanDeleteFile);
    body.append('deletedContractTemplate', contractTemplateDeleteFile);
    body.append('deletedBeoTemplate', beoTemplateDeleteFile);
    body.append('deletedInvoiceTemplate', invoiceTemplateDeleteFile);
    body.append('deletedProposalTemplate', proposalTemplateDeleteFile);
    body.append('deletedEnquiryTemplate', enquiryTemplateDeleteFile);
    body.append('deletedPiTemplate', piTemplateDeleteFile);
    body.append('deletedCorporateRateTemplate', corporateRateTemplateDeleteFile);
    return body;
  };

  const handleFormChange = (key: string, value: any, formData: any, isDeleted: any, formRef: any, setHasError: any) => {
    deleteImages(key, value, formData, isDeleted);
  };
  const customCallbackResponse = (data: any) => {
    ScrollTop(0);
    if (innerTabValue < languageList?.length - 1) {
      setInnerTabValue(innerTabValue + 1);
    } else {
      navigate(routes.organization);
    }
  };

  function idProps(index: number) {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    };
  }

  return (
    <>
      <Tabs
        value={innerTabValue}
        onChange={handleChange}
        aria-label="basic tabs example"
        variant="scrollable"
        allowScrollButtonsMobile
        scrollButtons="auto"
        sx={{
          '.MuiTabScrollButton-root.Mui-disabled': {
            display: 'none',
          },
        }}>
        {languageList?.map((item: any) => (
          <Tab key={item.value} label={item.text} {...idProps(item.value)} />
        ))}
      </Tabs>
      {languageList?.map((item: any, indexValue: number) => (
        <TabPanel key={item.value} value={innerTabValue} index={indexValue}>
          <Box py={1}>
            <AddEdit
              customCallbackResponse={customCallbackResponse}
              handlePropsChange={handleFormChange}
              pageConfig={pageConfig}
              pageResponse={pageResponse}
              handleCustomResponse={handleCustomResponse as any}></AddEdit>
          </Box>
        </TabPanel>
      ))}
    </>
  );
};
